import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { BookingButton } from '@/components/experiences/BookingButton';
import { useExperiencesStore } from '@/lib/stores/experiences-store';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { useModalStore } from '@/lib/stores/modal-store';
import { Shield } from 'lucide-react';

export function BookExperience() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUnifiedAuth();
  const { openAuthModal } = useModalStore();
  const { experiences } = useExperiencesStore();
  
  const experience = experiences.find((exp) => exp.id === id);
  
  if (!experience) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-sand-400">Experience not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 bg-earth-900">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Experience Details */}
            <div className="md:w-1/2">
              <img
                src={experience.media?.[0]?.url || 'https://via.placeholder.com/400x300'}
                alt={experience.title}
                className="w-full h-64 object-cover rounded-lg mb-6"
              />
              <h1 className="text-2xl font-display mb-4">{experience.title}</h1>
              <p className="text-sand-300 mb-6">{experience.description}</p>

              {experience.booking_type === 'request' && experience.requirements_description && (
                <div className="mt-6 p-4 bg-earth-800/50 rounded-lg">
                  <div className="flex items-center gap-2 text-sand-400">
                    <Shield className="h-5 w-5" />
                    <span className="text-sm">
                      {experience.booking_type === 'instant' ? 
                        'Secure instant booking powered by Stripe' : 
                        'Your request will be reviewed by the host'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Booking Section */}
            <div className="md:w-1/2">
              <div className="bg-earth-800/50 p-6 rounded-lg">
                <h2 className="text-xl font-display mb-6">
                  {experience.booking_type === 'instant' ? 
                    'Book this Experience' : 
                    'Request to Join'}
                </h2>

                <div className="border-t border-earth-700 pt-4">
                  <div className="flex justify-between text-lg font-semibold">
                    <span className="text-sand-100">Experience Price</span>
                    <span className="text-sand-100">${experience.price}</span>
                  </div>
                  <p className="text-sm text-sand-400 mt-2">
                    Final price may vary based on number of participants
                  </p>
                </div>

                {user ? (
                  <BookingButton
                    experience={experience}
                    quantity={1}
                    date=""
                    className="w-full mt-6"
                  />
                ) : (
                  <Button 
                    className="w-full mt-6"
                    onClick={() => openAuthModal('sign-up')}
                  >
                    Sign in to {experience.booking_type === 'instant' ? 'Book' : 'Request'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}